import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { proposalAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatINR, formatPercent, formatDate, getStatusBadgeClass, shortId, getMarginBadgeClass, apiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Edit2,
  Download,
  Clock,
  Calendar,
  FileText,
  Loader2,
  History
} from 'lucide-react';

export default function ProposalView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { can } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchProposal();
  }, [id]);

  const fetchProposal = async () => {
    try {
      const response = await proposalAPI.getOne(id);
      setProposal(response.data);
    } catch (error) {
      toast.error(apiError(error, 'Failed to fetch proposal'));
      navigate('/proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await proposalAPI.downloadPdf(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proposal_${shortId(id)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error(apiError(error, 'Failed to download PDF'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!proposal) {
    return null;
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/proposals')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">{proposal.project_name}</h1>
              <span className={`badge ${getStatusBadgeClass(proposal.status)}`}>
                {proposal.status}
              </span>
            </div>
            <p className="text-gray-500 mt-1">
              <span className="font-mono text-xs">{shortId(proposal.id)}</span> • {proposal.client_name}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          {can('write') && (
            <Button variant="outline" onClick={() => navigate(`/proposals/${id}/edit`)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          <Button 
            className="bg-black hover:bg-black/90"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Project Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-medium">{proposal.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Terms</p>
                  <p className="font-medium">{proposal.payment_terms}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Due Date</p>
                    <p className="font-medium">{formatDate(proposal.due_date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Version</p>
                    <p className="font-medium">v{proposal.version || 1}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Deliverables */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Scope of Work</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-sm font-medium text-gray-500">Deliverable</th>
                    <th className="text-center py-2 text-sm font-medium text-gray-500">Qty</th>
                    <th className="text-right py-2 text-sm font-medium text-gray-500">Unit Price</th>
                    <th className="text-right py-2 text-sm font-medium text-gray-500">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.deliverables?.map((item, index) => (
                    <tr key={index} className="border-b last:border-0">
                      <td className="py-3">{item.name}</td>
                      <td className="py-3 text-center">{item.quantity}</td>
                      <td className="py-3 text-right font-mono">{formatINR(item.unit_price)}</td>
                      <td className="py-3 text-right font-mono font-medium">{formatINR(item.quantity * item.unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(!proposal.deliverables || proposal.deliverables.length === 0) && (
                <div className="text-center py-8 text-gray-400">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No deliverables specified</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {proposal.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 whitespace-pre-wrap">{proposal.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Version History */}
          {proposal.versions && proposal.versions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Version History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {proposal.versions.map((version, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">Version {version.version}</p>
                        <p className="text-sm text-gray-500">{version.changes}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-gray-600">{version.created_by}</p>
                        <p className="text-gray-400">{formatDate(version.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Financial Summary */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Costs */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Labor Cost</span>
                  <span className="font-mono">{formatINR(proposal.labor_cost)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Overhead</span>
                  <span className="font-mono">{formatINR(proposal.overhead_cost)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Direct Costs</span>
                  <span className="font-mono">{formatINR(proposal.direct_cost)}</span>
                </div>
                <div className="flex justify-between font-medium pt-2 border-t">
                  <span>Total Cost (COGS)</span>
                  <span className="font-mono">{formatINR(proposal.total_cost)}</span>
                </div>
              </div>

              {/* Pricing */}
              <div className="pt-4 border-t space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Proposed Price</span>
                  <span className="font-mono">{formatINR(proposal.proposed_price)}</span>
                </div>
                {proposal.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount ({proposal.discount_percentage}%)</span>
                    <span className="font-mono">-{formatINR(proposal.discount_amount)}</span>
                  </div>
                )}
              </div>

              {/* Final */}
              <div className="bg-black text-white p-4 -mx-6 -mb-6 rounded-b-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Final Price</span>
                  <span className="text-2xl font-bold font-mono">{formatINR(proposal.final_price)}</span>
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/20">
                  <span className="text-sm text-gray-300">Gross Profit</span>
                  <span className="font-mono">{formatINR(proposal.final_price - proposal.total_cost)}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-300">Margin</span>
                  <span className={`badge ${getMarginBadgeClass(proposal.margin_percentage)}`}>
                    {formatPercent(proposal.margin_percentage)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Info */}
          {proposal.payment_schedule && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payment Schedule</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{proposal.payment_schedule}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
